// src/pages/preventivas/PreventivasAbertasPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useTheme } from "../../contexts/ThemeContext";
import { useAuth } from "../../contexts/AuthContext";
import { useIsMobile } from "../../hooks/useIsMobile";
import LayoutDesktop from "../../layouts/LayoutDesktop";
import LayoutMobile from "../../layouts/LayoutMobile";
import FiltroBar from "../../components/FiltroBar/FiltroBar";
import CardListMobile from "../../templates/mobile/CardListMobile";
import { IconHash, IconMachine, IconRepeat, IconRule, IconChevronRight, IconCalendar } from "../../components/Icons/Icons";
import { pode, temNivelMinimo } from "../../utils/permissoes";
import { NIVEIS } from "../../config/niveis";

import { db } from "../../config/firebase";
import {
  collection,
  deleteDoc,
  doc,
  getDocs,
  query,
  where,
} from "firebase/firestore";

import { formatData, timestampToDate } from "../../utils/dateFormat";
import { montarNomeExibicaoPreventiva, periodicidadeLabel } from "../../utils/preventivaNome";
import { ativarPreventivasPlanejadasAteHoje } from "../../services/abrirPreventivasClienteApi";

// Calcula a próxima data de execução com base na periodicidade
function calcularProximaData(preventiva, referencia) {
  if (!preventiva) return null;

  const periodicidade = preventiva.periodicidade || "MENSAL";
  const base = new Date(referencia || new Date());
  base.setHours(0, 0, 0, 0);

  if (periodicidade === "SEMANAL") {
    const alvo = preventiva.diaSemana ?? base.getDay();
    const prox = new Date(base.getTime());
    prox.setDate(prox.getDate() + 7);
    if (alvo != null) {
      const diff = (alvo - prox.getDay() + 7) % 7;
      prox.setDate(prox.getDate() + diff);
    }
    return prox;
  }

  // Para Mensal / Trimestral / Anual, usamos meses
  let meses = 1;
  if (periodicidade === "TRIMESTRAL") meses = 3;
  if (periodicidade === "ANUAL") meses = 12;

  const alvoDia = preventiva.diaMes || base.getDate();
  const proxMes = new Date(base.getTime());
  proxMes.setDate(1);
  proxMes.setMonth(proxMes.getMonth() + meses);

  const ultimoDiaMes = new Date(
    proxMes.getFullYear(),
    proxMes.getMonth() + 1,
    0
  ).getDate();

  const diaFinal = Math.min(alvoDia, ultimoDiaMes);
  proxMes.setDate(diaFinal);
  return proxMes;
}

function renderRegra(p) {
  if (p.periodicidade === "SEMANAL") {
    if (p.diaSemana == null) return "-";
    const nomes = [
      "Domingo",
      "Segunda",
      "Terça",
      "Quarta",
      "Quinta",
      "Sexta",
      "Sábado",
    ];
    return nomes[p.diaSemana] || "-";
  }
  if (p.diaMes != null) return `Dia ${p.diaMes}`;
  return "-";
}

function PreventivasAbertasPage() {
  const { tema, alterarTema } = useTheme();
  const { usuario: usuarioLogado } = useAuth();
  const podeIniciarExecucao = pode(usuarioLogado, "preventivas", "execucao", "iniciarExecucao");
  const podeExcluir = temNivelMinimo(usuarioLogado, NIVEIS.ADMIN);
  const isMobile = useIsMobile(900);
  const navigate = useNavigate();

  const [sidebarAberta, setSidebarAberta] = useState(false);
  const [preventivas, setPreventivas] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState("");
  // (Execução agora abre um formulário dedicado; não registramos diretamente aqui)

  const [campoFiltro, setCampoFiltro] = useState("nome");
  const [textoFiltro, setTextoFiltro] = useState("");
  const [expandedId, setExpandedId] = useState(null);

  const camposFiltro = [
    { value: "nome", label: "Nome" },
    { value: "empresaNome", label: "Empresa" },
    { value: "maquinaNome", label: "Máquina" },
    { value: "codigo", label: "Código" },
  ];

  useEffect(() => {
    async function carregarPreventivasAbertas() {
      if (!usuarioLogado?.grupoId) return;

      setCarregando(true);
      setErro("");

      try {
        await ativarPreventivasPlanejadasAteHoje({
          grupoId: usuarioLogado.grupoId,
          referencia: new Date(),
        });

        const ref = collection(db, "preventivas");
        const q = query(ref, where("grupoId", "==", usuarioLogado.grupoId));
        const snap = await getDocs(q);

        console.log("Total de preventivas no Firestore:", snap.docs.length);
        snap.docs.forEach((doc) => {
          console.log("Preventiva:", doc.id, doc.data());
        });

        const lista = snap.docs
          .map((docSnap) => ({
            id: docSnap.id,
            ...docSnap.data(),
          }))
          .filter((p) => {
            // Mostrar apenas preventivas ativas (não desativadas)
            return p.ativo !== false;
          })
          .sort((a, b) => {
            const da = timestampToDate(a.proximaData)?.getTime() || 0;
            const dbv = timestampToDate(b.proximaData)?.getTime() || 0;
            return da - dbv;
          });

        console.log("Preventivas abertas (após filtros):", lista);
        setPreventivas(lista);
      } catch (e) {
        console.error("Erro ao carregar preventivas abertas:", e);
        setErro("Erro ao carregar preventivas abertas. Tente novamente.");
      } finally {
        setCarregando(false);
      }
    }

    carregarPreventivasAbertas();
  }, [usuarioLogado]);

  const preventivasFiltradas = useMemo(() => {
    const texto = textoFiltro.trim().toLowerCase();
    if (!texto) return preventivas;

    return preventivas.filter((p) => {
      const valor = campoFiltro === "nome"
        ? montarNomeExibicaoPreventiva(p).toLowerCase()
        : String(p[campoFiltro] || "").toLowerCase();
      return valor.includes(texto);
    });
  }, [preventivas, campoFiltro, textoFiltro]);

  async function handleExcluir(preventiva) {
    if (!preventiva?.id) return;
    if (!podeExcluir) {
      alert("Acesso restrito.");
      return;
    }

    const confirmar = window.confirm(
      "Tem certeza que deseja excluir esta preventiva? Essa ação não pode ser desfeita."
    );
    if (!confirmar) return;

    setErro("");
    try {
      await deleteDoc(doc(db, "preventivas", preventiva.id));
      setPreventivas((prev) => prev.filter((p) => p.id !== preventiva.id));
    } catch (e) {
      console.error("Erro ao excluir preventiva:", e);
      setErro("Erro ao excluir preventiva. Tente novamente.");
    }
  }

  function handleAbrirExecucao(preventiva) {
    if (!preventiva?.id) return;
    if (!podeIniciarExecucao) {
      alert("Acesso restrito.");
      return;
    }
    navigate(`/preventivas/executar/${preventiva.id}`);
  }

  const itemsParaCards = preventivasFiltradas.map((p) => ({
    id: p.id,
    nome: montarNomeExibicaoPreventiva(p) || "Sem nome",
    subtitulo: p.empresaNome || "-",
    detalhes: [
      { icone: <IconHash size="14px" />, label: "Código", valor: p.codigo || "-" },
      { icone: <IconMachine size="14px" />, label: "Máquina", valor: p.maquinaNome || "-" },
      {
        icone: <IconCalendar size="14px" />,
        label: "Próxima Execução",
        valor: formatData(p.proximaData),
      },
      {
        icone: <IconRepeat size="14px" />,
        label: "Periodicidade",
        valor: p.periodicidade || "-",
      },
	      {
	        icone: (
	          <>
	            <IconRule size="14px" />
	            <IconChevronRight size="14px" />
	          </>
	        ),
	        label: "Regra",
	        valor: renderRegra(p),
	      },
    ],
  }));

  if (isMobile) {
    return (
      <LayoutMobile
        tema={tema}
        alterarTema={alterarTema}
        titulo="Preventivas Abertas"
        sidebarAberta={sidebarAberta}
        onToggleSidebar={() => setSidebarAberta(!sidebarAberta)}
      >
        <CardListMobile
          onAcaoPrimaria={(item) => handleAbrirExecucao({ id: item.id })}
          acaoPrimariaLabel="Abrir execução"
          acaoPrimariaIcon={<IconChevronRight size="16px" />}
          items={itemsParaCards}
          carregando={carregando}
          erro={erro}
          titulo="Preventivas abertas"
          searchPlaceholder="Buscar preventiva..."
          emptyMessage="Nenhuma preventiva ativa neste momento."
          onExcluir={podeExcluir ? (id) => handleExcluir({ id }) : undefined}
        />
      </LayoutMobile>
    );
  }

  return (
    <LayoutDesktop
      tema={tema}
      titulo="Preventivas Abertas"
      subtitulo="Preventivas / Abertas"
    >
      <section className="pc-card pc-table-card">
        <header className="pc-table-header">
          <div className="pc-table-header-text">
            <h3>Preventivas abertas</h3>
            <p>
              Lista de todas as preventivas ativas, prontas para execução.
            </p>
          </div>
        </header>

        <FiltroBar
          campos={camposFiltro}
          campoSelecionado={campoFiltro}
          onCampoChange={setCampoFiltro}
          textoFiltro={textoFiltro}
          onTextoChange={setTextoFiltro}
          placeholder="Filtrar preventivas abertas..."
          mostrarAdicionar={false}
        />

        <div className="pc-table-wrapper">
          {erro && (
            <div
              style={{
                background: "#7f1d1d",
                color: "#fecaca",
                padding: 8,
                borderRadius: 8,
                marginBottom: 12,
                fontSize: 14,
              }}
            >
              {erro}
            </div>
          )}

          {carregando ? (
            <p style={{ padding: 10 }}>Carregando preventivas abertas...</p>
          ) : preventivasFiltradas.length === 0 ? (
            <p style={{ padding: 10 }}>Nenhuma preventiva ativa neste momento.</p>
          ) : (
            <table className="pc-table pc-table-expandable">
              <thead>
                <tr>
                  <th style={{ width: 44 }} />
                  <th>Código</th>
                  <th>Nome</th>
                  <th>Empresa</th>
                  <th>Máquina</th>
                  <th>Próxima Execução</th>
                  <th style={{ textAlign: "right" }}>Ações</th>
                </tr>
              </thead>
              <tbody>
                {preventivasFiltradas.map((p) => {
                  const aberto = expandedId === p.id;

                  return (
                    <React.Fragment key={p.id}>
                      <tr className={aberto ? "pc-row-open" : undefined}>
                        <td>
                          <button
                            type="button"
                            className="pc-expand-btn"
                            aria-label={aberto ? "Recolher" : "Expandir"}
                            onClick={() => setExpandedId((prev) => (prev === p.id ? null : p.id))}
                          >
                            <span className={aberto ? "pc-expand-chevron open" : "pc-expand-chevron"}>
                              ▸
                            </span>
                          </button>
                        </td>
                        <td>{p.codigo || "-"}</td>
                        <td>{montarNomeExibicaoPreventiva(p)}</td>
                        <td>{p.empresaNome || "-"}</td>
                        <td>{p.maquinaNome || "-"}</td>
                        <td>{formatData(p.proximaData)}</td>
                        <td
                          style={{
                            textAlign: "right",
                            whiteSpace: "nowrap",
                          }}
                        >
                          <button
                            type="button"
                            className="pc-btn-primario"
                            style={{ fontSize: 13, padding: "4px 10px" }}
                            onClick={() => handleAbrirExecucao(p)}
                          >
                            Abrir execução
                          </button>
                          {podeExcluir ? (
                            <button
                              type="button"
                              className="pc-icon-btn"
                              title="Excluir preventiva"
                              style={{ marginLeft: 8 }}
                              onClick={() => handleExcluir(p)}
                            >
                              🗑️
                            </button>
                          ) : null}
                        </td>
                      </tr>

                      {aberto ? (
                        <tr className="pc-expanded-row">
                          <td colSpan={7}>
                            <div className="pc-expand-panel">
                              <div className="pc-expand-grid">
                                <div className="pc-expand-item">
                                  <div className="pc-expand-label">Periodicidade</div>
                                  <div className="pc-expand-value">{periodicidadeLabel(p.periodicidade) || "-"}</div>
                                </div>
                                <div className="pc-expand-item">
                                  <div className="pc-expand-label">Regra</div>
                                  <div className="pc-expand-value">{renderRegra(p)}</div>
                                </div>
                                <div className="pc-expand-item">
                                  <div className="pc-expand-label">Nome original</div>
                                  <div className="pc-expand-value">{p.nome || "-"}</div>
                                </div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      ) : null}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </section>
    </LayoutDesktop>
  );
}

export default PreventivasAbertasPage;